import { useEffect, useRef, useState } from 'react'

interface UseSingleAnimationStateReturn {
  isAnimating: boolean
  triggerAnimation: () => void
}

export const useSingleAnimationState = (): UseSingleAnimationStateReturn => {
  const [isAnimating, setIsAnimating] = useState<boolean>(false)
  const [retriggerAnimation, setRetriggerAnimation] = useState<boolean>(false)

  const triggerAnimation = (): void => {
    setIsAnimating(false)
    setRetriggerAnimation(true)
  }

  useEffect((): void => {
    // uses a retrigger flag and temporarily flip to false to know to reinitiate
    if (isAnimating === false && retriggerAnimation === true) {
      setIsAnimating(true)
      setRetriggerAnimation(false)
    }
  }, [isAnimating, retriggerAnimation])

  return {
    isAnimating,
    triggerAnimation,
  }
}

interface CircleCoordinates {
  x: number
  y: number
}

interface UseCursorExpandingCircleReturn<T> {
  isAnimating: boolean
  buttonRef: React.MutableRefObject<T | null>
  circleCoordinates: CircleCoordinates
  handleClick: (e: React.MouseEvent<T>) => void
}

export const useCursorExpandingCircle = <T extends HTMLElement>(
  radius: number,
  onClick?: (e: React.MouseEvent<T>) => void,
): UseCursorExpandingCircleReturn<T> => {
  const buttonRef: React.MutableRefObject<T | null> = useRef(null)
  const [circleCoordinates, setCircleCoordinates] = useState<CircleCoordinates>(
    { x: 0, y: 0 },
  )
  const { isAnimating, triggerAnimation } = useSingleAnimationState()

  const handleClick = (e: React.MouseEvent<T>): void => {
    if (buttonRef.current !== null) {
      const boundingClientRect = buttonRef.current?.getBoundingClientRect()
      setCircleCoordinates({
        x: e.clientX - boundingClientRect.left - radius,
        y: e.clientY - boundingClientRect.top - radius,
      })
      triggerAnimation()
    }
    if (onClick !== undefined) {
      onClick(e)
    }
  }

  return {
    isAnimating,
    buttonRef,
    circleCoordinates,
    handleClick,
  }
}
